import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Send, Paperclip, Image, FileText, Smile, Users, MoreVertical,
  CheckCheck, X, MessageCircle, Info, CheckSquare, BellOff, Ban,
  Trash2, Download, Clock, Phone, Video, Star, ThumbsDown, Mic,
} from 'lucide-react';
import EmojiPicker from 'emoji-picker-react';
import { useWhatsApp } from '../context/WhatsAppContext';
import API from '../api/axios';

/* ─── Chat Header Dropdown ─────────────────────────────────────────────────── */
const ChatHeaderMenu = ({ onSelect }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef();

  const options = [
    { label: 'Contact Info', icon: <Info size={16} />, action: 'info' },
    { label: 'Select messages', icon: <CheckSquare size={16} />, action: 'select' },
    { label: 'Mute notifications', icon: <BellOff size={16} />, action: 'mute' },
    { label: 'Block', icon: <Ban size={16} />, action: 'block' },
    { label: 'Clear chat', icon: <Trash2 size={16} />, action: 'clear' },
    { label: 'Export chat', icon: <Download size={16} />, action: 'export' },
    { label: 'Disappearing messages', icon: <Clock size={16} />, action: 'disappearing' },
  ];

  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="p-2 text-gray-600 hover:bg-gray-100 rounded-full transition"
      >
        <MoreVertical size={18} />
      </button>
      {open && (
        <div className="absolute right-0 mt-1 w-56 bg-white rounded-lg shadow-xl border border-gray-200 py-1 z-50">
          {options.map((opt) => (
            <button
              key={opt.action}
              onClick={() => { setOpen(false); onSelect(opt.action); }}
              className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 w-full text-left"
            >
              {opt.icon} {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

/* ─── Contact Info Panel ───────────────────────────────────────────────────── */
const ContactInfoPanel = ({ jid, name, onClose }) => {
  const [pic, setPic] = useState(null);
  const { BASE_URL } = useWhatsApp();

  useEffect(() => {
    if (!jid) return;
    fetch(`${BASE_URL}/profile-pic/${encodeURIComponent(jid)}`)
      .then(r => r.json())
      .then(d => setPic(d.url || null))
      .catch(() => setPic(null));
  }, [jid, BASE_URL]);

  const displayName = name || jid?.split('@')[0] || 'Unknown';
  const phone = jid?.split('@')[0] || '';

  return (
    <div className="fixed right-0 top-16 h-[calc(100vh-64px)] w-80 bg-white border-l border-gray-200 z-40 overflow-y-auto shadow-xl flex flex-col">
      <div className="flex justify-between items-center px-5 py-4 border-b">
        <h3 className="font-semibold text-gray-800">Contact Info</h3>
        <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-full">
          <X size={20} />
        </button>
      </div>

      <div className="flex flex-col items-center py-6 px-5 border-b">
        {pic ? (
          <img src={pic} alt="" className="w-24 h-24 rounded-full object-cover shadow-md" />
        ) : (
          <div className="w-24 h-24 rounded-full bg-green-500 flex items-center justify-center text-white text-3xl font-bold shadow-md">
            {displayName[0]?.toUpperCase()}
          </div>
        )}
        <h2 className="mt-3 text-lg font-semibold text-gray-800">{displayName}</h2>
        <p className="text-sm text-gray-500">+{phone}</p>
      </div>

      <div className="px-5 py-4 border-b space-y-1">
        <button className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-gray-50 w-full text-left text-sm text-gray-700">
          <Phone size={18} className="text-gray-400" /> Audio Call <span className="ml-auto text-xs text-gray-400">(unavailable)</span>
        </button>
        <button className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-gray-50 w-full text-left text-sm text-gray-700">
          <Video size={18} className="text-gray-400" /> Video Call <span className="ml-auto text-xs text-gray-400">(unavailable)</span>
        </button>
      </div>

      <div className="px-5 py-4 border-b">
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">About</p>
        <p className="text-sm text-gray-600">Hey there! I am using WhatsApp.</p>
      </div>

      <div className="px-5 py-4 space-y-1">
        <button className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-gray-50 w-full text-left text-sm text-gray-700">
          <BellOff size={18} className="text-gray-400" /> Mute notifications
        </button>
        <button className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-gray-50 w-full text-left text-sm text-gray-700">
          <Star size={18} className="text-gray-400" /> Starred messages
        </button>
        <button className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-gray-50 w-full text-left text-sm text-gray-700">
          <Trash2 size={18} className="text-gray-400" /> Clear chat
        </button>
        <button className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-gray-50 w-full text-left text-sm text-gray-700">
          <Ban size={18} className="text-gray-400" /> Block
        </button>
        <button className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-red-50 w-full text-left text-sm text-red-600">
          <ThumbsDown size={18} /> Report contact
        </button>
      </div>
    </div>
  );
};

/* ─── Message Bubble ───────────────────────────────────────────────────────── */
const MessageBubble = ({ msg }) => {
  const time = msg.timestamp
    ? new Date(msg.timestamp * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : '';

  const renderContent = () => {
    if (msg.text?.startsWith('[Image]') || msg.mediaType === 'image') {
      return (
        <div className="flex items-center gap-2 text-gray-500 italic">
          <Image size={16} /> <span>Image</span>
        </div>
      );
    }
    if (msg.text?.startsWith('[Document]') || msg.mediaType === 'document') {
      return (
        <div className="flex items-center gap-2 text-gray-500">
          <FileText size={16} />
          <span className="truncate max-w-[200px]">
            {msg.text?.replace('[Document] ', '') || 'Document'}
          </span>
        </div>
      );
    }
    if (msg.text?.startsWith('[Audio]') || msg.mediaType === 'audio') {
      return (
        <div className="flex items-center gap-2 text-gray-500 italic">
          <Mic size={16} /> <span>Audio</span>
        </div>
      );
    }
    if (msg.text?.startsWith('[Video]') || msg.mediaType === 'video') {
      return (
        <div className="flex items-center gap-2 text-gray-500 italic">
          <span>🎥 Video</span>
        </div>
      );
    }
    return <p className="break-words whitespace-pre-wrap">{msg.text}</p>;
  };

  const StatusIcon = () => {
    if (!msg.isMine) return null;
    if (msg.status === 'pending') return <Clock size={13} className="text-gray-400" />;
    if (msg.status === 'sent') return <CheckCheck size={13} className="text-gray-400" />;
    if (msg.status === 'delivered') return <CheckCheck size={13} className="text-gray-500" />;
    if (msg.status === 'read') return <CheckCheck size={13} className="text-blue-500" />;
    return <CheckCheck size={13} className="text-gray-400" />;
  };

  return (
    <div className={`flex ${msg.isMine ? 'justify-end' : 'justify-start'} animate-fadeInUp`}>
      <div className="max-w-[75%]">
        {!msg.isMine && msg.senderName && (
          <p className="text-xs font-medium text-green-600 mb-0.5 ml-2">
            {msg.senderName}
          </p>
        )}
        <div
          className={`relative px-3.5 py-2 rounded-2xl shadow-sm text-sm leading-relaxed ${
            msg.isMine
              ? 'bg-[#dcf8c6] text-gray-800 rounded-br-sm'
              : 'bg-white text-gray-800 rounded-bl-sm'
          }`}
        >
          {renderContent()}
          <div className="flex justify-end items-center gap-1 mt-1">
            <span className="text-[10px] text-gray-400">{time}</span>
            <StatusIcon />
          </div>
        </div>
      </div>
    </div>
  );
};

/* ─── Main Chat Page ───────────────────────────────────────────────────────── */
const ChatPage = () => {
  const { activeChat, contacts, messages, addSentMessage, BASE_URL } = useWhatsApp();
  const [input, setInput] = useState('');
  const [file, setFile] = useState(null);
  const [sending, setSending] = useState(false);
  const [showBulk, setShowBulk] = useState(false);
  const [bulkNumbers, setBulkNumbers] = useState([]);
  const [bulkInput, setBulkInput] = useState('');
  const [showEmoji, setShowEmoji] = useState(false);
  const [showInfoPanel, setShowInfoPanel] = useState(false);
  const [headerPic, setHeaderPic] = useState(null);
  const [toast, setToast] = useState(null);

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const emojiRef = useRef(null);

  const activeMessages = activeChat ? messages[activeChat] || [] : [];
  const activeContact = contacts.find(c => c.jid === activeChat);

  const showToast = useCallback((msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  }, []);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [activeMessages]);

  // Fetch header profile pic
  useEffect(() => {
    if (!activeChat) { setHeaderPic(null); return; }
    fetch(`${BASE_URL}/profile-pic/${encodeURIComponent(activeChat)}`)
      .then(r => r.json())
      .then(d => setHeaderPic(d.url || null))
      .catch(() => setHeaderPic(null));
  }, [activeChat, BASE_URL]);

  // Close emoji picker on outside click
  useEffect(() => {
    const handler = (e) => {
      if (emojiRef.current && !emojiRef.current.contains(e.target)) {
        setShowEmoji(false);
      }
    };
    if (showEmoji) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showEmoji]);

  const sendText = async () => {
    if (!activeChat || !input.trim() || sending) return;
    const number = activeChat.replace('@s.whatsapp.net', '').replace('@g.us', '');
    const msgText = input.trim();
    setSending(true);
    setInput('');
    try {
      const endpoint = activeChat.includes('@g.us') ? '/send-group-message' : '/send-message';
      const payload = activeChat.includes('@g.us')
        ? { groupJid: activeChat, message: msgText }
        : { number, message: msgText };

      const res = await API.post(endpoint, payload);
      if (res.data.success) {
        addSentMessage(activeChat, {
          id: res.data.messageId || `local-${Date.now()}`,
          text: msgText,
          isMine: true,
          timestamp: Math.floor(Date.now() / 1000),
          sender: 'me',
          senderName: 'You',
          status: 'sent',
        });
      }
    } catch (err) {
      showToast('Send failed: ' + (err.response?.data?.error || err.message), 'error');
      setInput(msgText); // restore input on failure
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  const sendMedia = async () => {
    if (!activeChat || !file || sending) return;
    const number = activeChat.replace('@s.whatsapp.net', '');
    const formData = new FormData();
    formData.append('number', number);

    let endpoint = '/send-document';
    let msgText = `[Document] ${file.name}`;

    if (file.type.startsWith('image/')) {
      formData.append('image', file);
      formData.append('caption', input || '');
      endpoint = '/send-image';
      msgText = input || '[Image]';
    } else if (file.type.startsWith('audio/')) {
      formData.append('audio', file);
      endpoint = '/send-audio';
      msgText = '[Audio]';
    } else {
      formData.append('document', file);
    }

    setSending(true);
    try {
      const res = await API.post(endpoint, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (res.data.success) {
        addSentMessage(activeChat, {
          id: res.data.messageId || `local-${Date.now()}`,
          text: msgText,
          isMine: true,
          timestamp: Math.floor(Date.now() / 1000),
          sender: 'me',
          senderName: 'You',
          status: 'sent',
          mediaType: file.type.startsWith('image/') ? 'image' : file.type.startsWith('audio/') ? 'audio' : 'document',
        });
        setFile(null);
        setInput('');
        showToast('File sent!');
      }
    } catch (err) {
      showToast('Send failed: ' + (err.response?.data?.error || err.message), 'error');
    } finally {
      setSending(false);
    }
  };

  const handleBulkSend = async () => {
    if (bulkNumbers.length === 0 || !input.trim()) return;
    setSending(true);
    try {
      const res = await API.post('/send-bulk', { numbers: bulkNumbers, message: input });
      const results = res.data.results || [];
      const success = results.filter(r => r.success).length;
      const failed = results.filter(r => !r.success).length;
      showToast(`Sent: ${success} ✓  Failed: ${failed} ✗`);
      setBulkNumbers([]);
      setShowBulk(false);
      setInput('');
    } catch (err) {
      showToast('Bulk send failed: ' + (err.response?.data?.error || err.message), 'error');
    } finally {
      setSending(false);
    }
  };

  const handleMenuAction = (action) => {
    if (action === 'info') setShowInfoPanel(true);
    if (action === 'clear') {
      // Clear local messages for this chat
      showToast('Chat cleared locally');
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (file) sendMedia();
      else sendText();
    }
  };

  return (
    <div className="flex flex-col h-full bg-[#efeae2] relative">
      {/* Toast notification */}
      {toast && (
        <div className={`fixed top-20 right-4 z-50 px-4 py-2.5 rounded-lg shadow-lg text-sm font-medium text-white transition-all ${
          toast.type === 'error' ? 'bg-red-500' : 'bg-green-600'
        }`}>
          {toast.msg}
        </div>
      )}

      {activeChat ? (
        <>
          {/* ── Chat Header ── */}
          <div className="flex items-center justify-between px-4 py-3 bg-[#f0f2f5] border-b border-gray-200 shadow-sm flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className="relative flex-shrink-0">
                {headerPic ? (
                  <img src={headerPic} alt="" className="w-10 h-10 rounded-full object-cover" />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center text-white font-bold text-lg">
                    {(activeContact?.name || activeChat)[0]?.toUpperCase()}
                  </div>
                )}
                <span className="absolute bottom-0 right-0 w-3 h-3 bg-green-400 border-2 border-white rounded-full" />
              </div>
              <div>
                <h2 className="font-semibold text-gray-800 text-sm leading-tight">
                  {activeContact?.name || activeChat.split('@')[0]}
                </h2>
                <p className="text-xs text-gray-500">
                  {activeChat.includes('@g.us') ? 'Group' : 'online'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setShowBulk(true)}
                className="p-2 text-gray-600 hover:bg-gray-200 rounded-full transition"
                title="Bulk send"
              >
                <Users size={18} />
              </button>
              <ChatHeaderMenu onSelect={handleMenuAction} />
            </div>
          </div>

          {/* ── Messages Area ── */}
          <div
            className="flex-1 overflow-y-auto px-4 py-3 space-y-1.5"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23a0a0a0' fill-opacity='0.07'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            }}
          >
            {activeMessages.length === 0 && (
              <div className="flex flex-col items-center justify-center h-full text-gray-400 py-10">
                <MessageCircle size={40} className="mb-2 opacity-30" />
                <p className="text-sm">No messages yet. Say hello! 👋</p>
              </div>
            )}
            {activeMessages.map((msg, i) => (
              <MessageBubble key={msg.id || i} msg={msg} />
            ))}
            <div ref={messagesEndRef} />
          </div>

          {/* ── File Preview ── */}
          {file && (
            <div className="mx-4 mb-2 p-2.5 bg-white border border-gray-200 rounded-xl flex items-center gap-3 shadow-sm flex-shrink-0">
              {file.type.startsWith('image/') ? (
                <img
                  src={URL.createObjectURL(file)}
                  alt="preview"
                  className="w-12 h-12 rounded-lg object-cover"
                />
              ) : (
                <div className="w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center">
                  <FileText size={22} className="text-gray-400" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm text-gray-700 truncate font-medium">{file.name}</p>
                <p className="text-xs text-gray-400">{(file.size / 1024).toFixed(1)} KB</p>
              </div>
              <button
                onClick={() => setFile(null)}
                className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full transition"
              >
                <X size={16} />
              </button>
            </div>
          )}

          {/* ── Input Area ── */}
          <div className="px-4 py-3 bg-[#f0f2f5] border-t border-gray-200 flex items-end gap-2 flex-shrink-0 relative">
            {/* Emoji Picker */}
            {showEmoji && (
              <div ref={emojiRef} className="absolute bottom-full left-4 mb-2 z-50 shadow-2xl rounded-xl overflow-hidden">
                <EmojiPicker
                  onEmojiClick={(emoji) => {
                    setInput(prev => prev + emoji.emoji);
                    inputRef.current?.focus();
                  }}
                  height={380}
                  width={320}
                  searchDisabled={false}
                  skinTonesDisabled
                  previewConfig={{ showPreview: false }}
                />
              </div>
            )}

            <button
              onClick={() => document.getElementById('chat-file-input')?.click()}
              className="p-2.5 text-gray-500 hover:bg-gray-200 rounded-full transition flex-shrink-0"
              title="Attach file"
            >
              <Paperclip size={20} />
            </button>
            <input
              id="chat-file-input"
              type="file"
              hidden
              onChange={(e) => {
                if (e.target.files[0]) setFile(e.target.files[0]);
                e.target.value = '';
              }}
            />

            <div className="flex-1 relative">
              <textarea
                ref={inputRef}
                rows={1}
                placeholder="Type a message…"
                className="w-full py-2.5 pl-4 pr-10 bg-white border border-gray-200 text-gray-800 rounded-2xl outline-none focus:ring-2 focus:ring-green-300 placeholder-gray-400 text-sm resize-none overflow-hidden leading-relaxed"
                style={{ maxHeight: '120px' }}
                value={input}
                onChange={(e) => {
                  setInput(e.target.value);
                  e.target.style.height = 'auto';
                  e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
                }}
                onKeyDown={handleKeyDown}
              />
              <button
                onClick={() => setShowEmoji(!showEmoji)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-yellow-500 transition"
                title="Emoji"
              >
                <Smile size={18} />
              </button>
            </div>

            {file ? (
              <button
                onClick={sendMedia}
                disabled={sending}
                className="p-2.5 bg-green-600 hover:bg-green-700 text-white rounded-full shadow transition disabled:opacity-50 flex-shrink-0"
                title="Send file"
              >
                <Send size={18} />
              </button>
            ) : (
              <button
                onClick={sendText}
                disabled={sending || !input.trim()}
                className="p-2.5 bg-green-600 hover:bg-green-700 text-white rounded-full shadow transition disabled:opacity-50 flex-shrink-0"
                title="Send message"
              >
                <Send size={18} />
              </button>
            )}
          </div>

          {/* ── Bulk Send Modal ── */}
          {showBulk && (
            <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 z-50">
              <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-md">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-gray-800">Bulk Send</h3>
                  <button onClick={() => setShowBulk(false)} className="p-1 hover:bg-gray-100 rounded-full">
                    <X size={20} />
                  </button>
                </div>

                <p className="text-sm text-gray-500 mb-3">
                  Add numbers and type a message below to send to multiple contacts.
                </p>

                {/* Number tags */}
                <div className="flex flex-wrap gap-2 mb-3 min-h-[36px]">
                  {bulkNumbers.map(n => (
                    <span key={n} className="inline-flex items-center gap-1 px-2.5 py-1 bg-green-50 text-green-700 border border-green-200 rounded-full text-sm">
                      {n}
                      <button onClick={() => setBulkNumbers(prev => prev.filter(x => x !== n))}>
                        <X size={12} />
                      </button>
                    </span>
                  ))}
                </div>

                {/* Add number input */}
                <div className="flex gap-2 mb-4">
                  <input
                    type="text"
                    placeholder="Phone number (with country code)"
                    value={bulkInput}
                    onChange={e => setBulkInput(e.target.value.replace(/\D/g, ''))}
                    onKeyDown={e => {
                      if (e.key === 'Enter' && bulkInput.trim()) {
                        setBulkNumbers(prev => [...new Set([...prev, bulkInput.trim()])]);
                        setBulkInput('');
                      }
                    }}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-green-300"
                  />
                  <button
                    onClick={() => {
                      if (bulkInput.trim()) {
                        setBulkNumbers(prev => [...new Set([...prev, bulkInput.trim()])]);
                        setBulkInput('');
                      }
                    }}
                    className="px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium"
                  >
                    Add
                  </button>
                </div>

                {/* Message */}
                <textarea
                  rows={3}
                  placeholder="Message to send…"
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-green-300 mb-4 resize-none"
                />

                <button
                  onClick={handleBulkSend}
                  disabled={bulkNumbers.length === 0 || !input.trim() || sending}
                  className="w-full py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition disabled:bg-gray-200 disabled:text-gray-400"
                >
                  {sending ? 'Sending…' : `Send to ${bulkNumbers.length} number${bulkNumbers.length !== 1 ? 's' : ''}`}
                </button>
              </div>
            </div>
          )}
        </>
      ) : (
        /* ── Empty State ── */
        <div className="flex-1 flex flex-col items-center justify-center bg-[#f8f9fa]">
          <div className="w-28 h-28 rounded-full bg-gradient-to-br from-green-400 to-green-600 flex items-center justify-center mb-6 shadow-xl">
            <MessageCircle size={52} className="text-white" />
          </div>
          <h2 className="text-2xl font-bold text-gray-600 mb-2">WhatsApp Web</h2>
          <p className="text-gray-400 text-center max-w-xs text-sm leading-relaxed">
            Select a contact from the sidebar to start chatting, or use the{' '}
            <strong className="text-gray-500">+</strong> button to start a new conversation.
          </p>
        </div>
      )}

      {/* ── Contact Info Panel ── */}
      {showInfoPanel && activeChat && (
        <ContactInfoPanel
          jid={activeChat}
          name={activeContact?.name}
          onClose={() => setShowInfoPanel(false)}
        />
      )}
    </div>
  );
};

export default ChatPage;
